import {
  ArtifactMetadataVersion as ArtifactMetadataVersionBase,
  ReferenceImage,
  ReferenceImageMetadata,
  Shot,
  ShotMetadata,
} from "@/types/artifact";
import { Attachment } from "@/types/attachment";
export type { Attachment };

export enum AvatarAudioType {
  None = "",
  ExtractMusic = "extract_music",
  Generate = "generate",
}

export type MediaType = "image" | "video" | "avatar";

export type ArtifactType =
  | "imageStoryboard"
  | "videoStoryboard"
  | "referenceImage";

export type ArtifactDataItem = Shot | ReferenceImage;

export type ArtifactMetadata = ShotMetadata | ReferenceImageMetadata;

export type ArtifactMetadataVersion = ArtifactMetadataVersionBase;

export type MediaSource = "upload" | "generate" | "ref-video";

export type MediaStatus = "generating" | "success" | "failed";

export interface MediaVersion {
  id: string;
  src: string;
  previewSrc: string;
  filePath: string;
  source: MediaSource;
  prompt: string;
  attachmentList: Attachment[];
  model?: ModelViewInfo;
  dialogue?: string;
  voiceId?: string;
  shotDuration?: number;
  status: MediaStatus;
  createdTime: number;
  avatarAudioType?: AvatarAudioType;
  videoClipStartTime?: number;
  viewUrl?: string;
  viewThumbnail?: string;
}

interface BaseMediaEditorItem {
  current: MediaVersion;
  versions: MediaVersion[];
  dataId?: string;
  metadata?: ArtifactMetadata;
}

export interface ImageMediaEditorItem extends BaseMediaEditorItem {
  mediaType: "image";
}

export interface VideoMediaEditorItem extends BaseMediaEditorItem {
  mediaType: "video";
}

export interface AvatarMediaEditorItem extends BaseMediaEditorItem {
  mediaType: "avatar";
}

export type MediaEditorItem =
  | ImageMediaEditorItem
  | VideoMediaEditorItem
  | AvatarMediaEditorItem;

interface BaseRegenerateParams {
  attachmentList: Attachment[];
  prompt: string;
  model: string;
  count: number;
}

export interface ImageRegenerateParams extends BaseRegenerateParams {
  mediaType: "image";
}

export interface VideoRegenerateParams extends BaseRegenerateParams {
  mediaType: "video";
}

export interface AvatarRegenerateParams extends BaseRegenerateParams {
  mediaType: "avatar";
  dialogue?: string;
  voiceId?: string;
  avatarAudioType?: AvatarAudioType;
}

export type RegenerateParams =
  | ImageRegenerateParams
  | VideoRegenerateParams
  | AvatarRegenerateParams;

export interface ImageModelInfo {
  name: string;
  display_name: string;
  text2image: boolean;
  image2image: boolean;
  images2image: boolean;
  price_items: {
    unit_type: "images";
    price: {
      output: number;
    };
  }[];
}

export interface VideoModelInfo {
  name: string;
  display_name: string;
  text2video: boolean;
  image2video: boolean;
  images2video: boolean;
  is_avatar_model: boolean;
  price_items: {
    unit_type: "videos" | "seconds";
    properties: {
      resolution: "720p" | "1080p";
    };
    price: {
      output: number;
    };
  }[];
  duration_options: number[];
}

interface BaseModelViewInfo {
  value: string;
  label: string;
  description: string;
  modelIds: string[];
  creditCost: number;
}

export interface ImageModelViewInfo extends BaseModelViewInfo {
  text2image: boolean;
  image2image: boolean;
  images2image: boolean;
}

export interface VideoModelViewInfo extends BaseModelViewInfo {
  text2video: boolean;
  image2video: boolean;
  images2video: boolean;
  isAvatarModel: boolean;
  durationOptions: number[];
  unitType: "videos" | "seconds";
}

export type ModelViewInfo = ImageModelViewInfo | VideoModelViewInfo;

export interface MediaViewState {
  manuallyAddedVersions: Record<number, MediaVersion[]>;
  selectedVersions: Record<number, string>;
  metadataOverrides: Record<number, Partial<MediaVersion>>;
}
